import { Html5Qrcode } from "html5-qrcode";
import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageShell, {
  trackEvent,
  trackBadgeScan,
  trackError,
  buildDigitalBackpackUrl,
} from "../components/PageShell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { API_BASE_URL, DIGITAL_BACKPACK_BASE_URL } from "../config/environment";
import {
  faCircleCheck as faCircleCheckRegular,
  faHandshake,
} from "@fortawesome/free-regular-svg-icons";
import {
  faQrcode,
  faSpinner,
  faXmark,
  faCheck,
  faIdCard,
  faTriangleExclamation,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";

const BADGE_STORAGE_KEY = "attendeeBadgeInfo";
const CONNECTION_DATA_KEY = "connectionData";

const persistShowCodes = (esc?: string, csc?: string) => {
  if (!esc && !csc) {
    return;
  }

  try {
    const existingConnectionData = localStorage.getItem(CONNECTION_DATA_KEY);
    const parsedConnectionData = existingConnectionData
      ? JSON.parse(existingConnectionData)
      : {};

    localStorage.setItem(
      CONNECTION_DATA_KEY,
      JSON.stringify({
        ...parsedConnectionData,
        ...(esc ? { esc } : {}),
        ...(csc ? { csc } : {}),
      }),
    );
  } catch (error) {
    console.error("Error persisting show codes:", error);
  }
};

function parseBadgeUrl(urlString: string) {
  try {
    // If the QR code is a redirect URL with a ?qr= param, extract and decode it
    const url = new URL(urlString);
    const qrParam = url.searchParams.get("qr");
    let badgeUrl = urlString;
    if (qrParam) {
      badgeUrl = decodeURIComponent(qrParam);
    }

    // parse the badge URL
    // Example: HTTPS://L4E.US/BLO/1274/8TEST/RICKY-RICK/TESTTEST
    // Format: l4e.us/showcode/badgeid/lastname/firstName/company
    const badge = new URL(badgeUrl);
    const parts = badge.pathname
      .split("/")
      .filter(Boolean)
      .map((part) => decodeURIComponent(part));

    //extract info
    let badgeId, lastName, firstName, companyName, showCode;
    if (parts.length >= 5) {
      // Extract showCode (first segment) and attendee info
      showCode = parts[0]; // BLO
      badgeId = parts[1]; // 1274
      lastName = parts[2]; // 8TEST
      firstName = parts[3]; // RICKY-RICK
      companyName = parts[4]; // TESTTEST
    } else {
      throw new Error("QR code format invalid");
    }

    return {
      showCode,
      badgeId,
      lastName,
      firstName,
      companyName,
      raw: badgeUrl,
    };
  } catch (e) {
    console.error("Error parsing badge URL:", e);
    return null;
  }
}

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showScanner, setShowScanner] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [showManualDrawer, setShowManualDrawer] = useState(false);
  const [manualBadgeId, setManualBadgeId] = useState("");
  const [manualLastName, setManualLastName] = useState("");
  const [manualError, setManualError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showProcessingSpinner, setShowProcessingSpinner] = useState(true); // Start with processing
  const [showLandingContent, setShowLandingContent] = useState(false);
  const [showSuccessMessage] = useState(false);
  const [exhibitorError, setExhibitorError] = useState(false);
  const [validationError, setValidationError] = useState(false); // Add validation error state
  const [serviceNotAvailable, setServiceNotAvailable] = useState(false); // Add service unavailable state
  const scannerRef = useRef<HTMLDivElement>(null);
  const qrScannerRef = useRef<Html5Qrcode | null>(null); // Add ref for QR scanner instance

  // New: attendee state from URL
  const [attendee, setAttendee] = useState<{
    firstName?: string;
    lastName?: string;
    title?: string;
    company?: string;
    badgeId?: string;
    showId?: string;
  }>({});

  useEffect(() => {
    const esc = (searchParams.get("esc") || "").trim();
    const csc = (searchParams.get("csc") || "").trim();

    // Persist show codes from URL immediately so other pages/components can
    // use authoritative values from connectionData during initial load.
    persistShowCodes(esc, csc);

    // Check if we have a qr parameter with the full badge URL
    const qrParam = searchParams.get("qr");

    let scannedAttendee: {
      showId?: string;
      badgeId?: string;
      firstName?: string;
      lastName?: string;
      company?: string;
      esc?: string;
      csc?: string;
    } = {};

    if (qrParam) {
      // Parse the badge URL from the qr parameter
      const badgeObj = parseBadgeUrl(qrParam);

      if (badgeObj) {
        // Store raw values WITH hyphens for API calls
        scannedAttendee = {
          showId: badgeObj.showCode,
          badgeId: badgeObj.badgeId,
          firstName: badgeObj.firstName, // Keep hyphens for API
          lastName: badgeObj.lastName, // Keep hyphens for API
          company: badgeObj.companyName, // Keep hyphens for API
          esc,
          csc,
        };
      } else {
        console.warn("Failed to parse badge URL from qr parameter");
      }
    } else {
      // Fall back to individual URL params (legacy support)
      const showId = searchParams.get("showId") || undefined;
      const badgeId = searchParams.get("badgeId") || undefined;
      const firstName = searchParams.get("firstName") || undefined;
      const lastName = searchParams.get("lastName") || undefined;
      const companyName = searchParams.get("companyName") || undefined;

      // Store raw values WITH hyphens for API calls
      scannedAttendee = {
        showId,
        badgeId,
        firstName, // Keep hyphens for API
        lastName, // Keep hyphens for API
        company: companyName, // Keep hyphens for API
        esc,
        csc,
      };
    }

    const exhibitorErrorParam = searchParams.get("exhibitorError");

    setAttendee(scannedAttendee);

    // Set exhibitor error state if present in URL
    if (exhibitorErrorParam === "true") {
      setExhibitorError(true);
      setShowProcessingSpinner(false);
      setShowLandingContent(true);
      return;
    }

    // Always validate first if we have badge data from URL
    const { showId, badgeId, firstName, lastName, company } = scannedAttendee;

    if (badgeId && (firstName || lastName)) {
      // Track badge scan from URL
      trackBadgeScan(badgeId, "qr");

      // Give a brief moment for the processing screen to show
      setTimeout(async () => {
        // STEP 1: Always validate the scanned badge first
        const showCode = showId || "";
        const badgeUrl = `https://l4e.us/${showCode}/${badgeId}/${
          lastName || "UNKNOWN"
        }/${firstName || "UNKNOWN"}/${company || "UNKNOWN"}`;
        // Note: No .replace(/ /g, "-") needed - data already has hyphens

        try {
          const validationResponse = await fetch(`${API_BASE_URL}/validation`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              badge: badgeUrl,
            }),
          });

          if (!validationResponse.ok) {
            const errorText = await validationResponse.text();

            // Track validation failure
            trackError("badge_validation_failed", errorText, {
              badge_id: badgeId,
              show_code: showCode,
            });

            // Check if it's a service not available error
            if (
              validationResponse.status === 400 ||
              validationResponse.status === 403 ||
              validationResponse.status === 404
            ) {
              try {
                const errorData = JSON.parse(errorText);

                // Check for service not available
                if (errorData.code === "ATTCON_SERVICE_NOT_AVAILABLE") {
                  setServiceNotAvailable(true);
                  setShowProcessingSpinner(false);
                  setShowLandingContent(true);
                  return;
                }

                // Check if it's an exhibitor error
                if (
                  errorData.code === "INVALID_REG_TYPE" ||
                  (errorData.message &&
                    (errorData.message.toLowerCase().includes("exhibitor") ||
                      errorData.message
                        .toLowerCase()
                        .includes("cannot create connections")))
                ) {
                  setExhibitorError(true);
                  setShowProcessingSpinner(false);
                  setShowLandingContent(true);
                  return;
                }
              } catch (parseError) {
                // If JSON parsing fails, check plain text error message
                if (
                  errorText.includes("ATTCON_SERVICE_NOT_AVAILABLE") ||
                  errorText.toLowerCase().includes("service is not available")
                ) {
                  setServiceNotAvailable(true);
                  setShowProcessingSpinner(false);
                  setShowLandingContent(true);
                  return;
                }

                if (
                  errorText.toLowerCase().includes("exhibitor") ||
                  errorText.includes("INVALID_REG_TYPE") ||
                  errorText.toLowerCase().includes("cannot create connections")
                ) {
                  setExhibitorError(true);
                  setShowProcessingSpinner(false);
                  setShowLandingContent(true);
                  return;
                }
              }
            }

            // Other validation errors - set validation error state
            setValidationError(true);
            setShowProcessingSpinner(false);
            setShowLandingContent(true);
            return;
          }

          // STEP 2: Validation passed - check for cached user data and auto-connect
          // Check if user badge is already cached in connectionData first
          const connectionDataStr = localStorage.getItem(CONNECTION_DATA_KEY);
          let userBadgeFromConnection = null;

          if (connectionDataStr) {
            try {
              const connectionData = JSON.parse(connectionDataStr);
              if (connectionData && connectionData.userBadge) {
                userBadgeFromConnection = connectionData.userBadge;
              }
            } catch (error) {
              console.error("Error parsing connection data:", error);
            }
          }

          // If we have userBadge in connectionData, proceed with automatic connection
          if (userBadgeFromConnection) {
            setIsLoading(true);
            try {
              await sendAttendeeInfoToApi(
                {
                  badgeId: userBadgeFromConnection.id,
                  lastName: userBadgeFromConnection.lastName,
                  firstName: userBadgeFromConnection.firstName,
                  companyName: userBadgeFromConnection.companyName,
                  showCode: userBadgeFromConnection.showCode || "",
                },
                scannedAttendee,
                setManualError,
                navigate,
                setIsLoading,
                setExhibitorError,
              );
              // If successful, we'll navigate away and won't reach this point
              return;
            } catch (error) {
              console.error("Auto-connection failed:", error);
              // If auto-connection fails, continue to show landing page
            }
            setIsLoading(false);
          } else {
            // Fall back to checking cached user badge from BADGE_STORAGE_KEY
            const cachedUserBadge = localStorage.getItem(BADGE_STORAGE_KEY);
            if (cachedUserBadge) {
              try {
                const userBadgeObj = JSON.parse(cachedUserBadge);
                setIsLoading(true);
                try {
                  await sendAttendeeInfoToApi(
                    userBadgeObj,
                    scannedAttendee,
                    setManualError,
                    navigate,
                    setIsLoading,
                    setExhibitorError,
                  );
                  // If successful, we'll navigate away and won't reach this point
                  return;
                } catch (error) {
                  console.error("Auto-connection failed:", error);
                  // If auto-connection fails, continue to show landing page
                }
                setIsLoading(false);
              } catch (error) {
                console.error("Error parsing cached user badge:", error);
                localStorage.removeItem(BADGE_STORAGE_KEY);
              }
            }
          }

          // If we reach here, validation passed but no cached user data or auto-connection failed
          // Show the landing page normally
          setShowProcessingSpinner(false);
          setShowLandingContent(true);
        } catch (error) {
          console.error("Error validating badge:", error);
          trackError(
            "badge_validation_error",
            error instanceof Error ? error.message : String(error),
            {
              badge_id: badgeId,
            },
          );
          // On validation error, set validation error state
          setValidationError(true);
          setShowProcessingSpinner(false);
          setShowLandingContent(true);
        }
      }, 500);
      return;
    }

    // If no badge data in URL, show landing page immediately (no validation needed)
    setTimeout(() => {
      setShowProcessingSpinner(false);
      setShowLandingContent(true);
    }, 500);
  }, [searchParams, navigate]);

  const startScanner = () => {
    trackEvent("qr_scanner_opened", {
      page_path: window.location.pathname,
    });

    setShowScanner(true);
    setPermissionDenied(false);
    // Add small delay to ensure DOM element is rendered
    setTimeout(() => {
      if (scannerRef.current) {
        const html5QrCode = new Html5Qrcode("qr-reader");
        qrScannerRef.current = html5QrCode; // Store the scanner instance
        html5QrCode
          .start(
            { facingMode: "environment" },
            { fps: 10, qrbox: 250 },
            async (decodedText) => {
              const badgeObj = parseBadgeUrl(decodedText);
              if (badgeObj) {
                html5QrCode.stop();
                qrScannerRef.current = null; // Clear the ref after stopping
                // Clear error state only after a valid QR code is decoded
                setExhibitorError(false);
                // Track successful QR scan
                trackBadgeScan(badgeObj.badgeId, "qr");
                setShowScanner(false);

                // Show processing spinner
                setShowProcessingSpinner(true);

                localStorage.setItem(
                  BADGE_STORAGE_KEY,
                  JSON.stringify(badgeObj),
                );

                setIsLoading(true);
                await sendAttendeeInfoToApi(
                  badgeObj,
                  attendee,
                  setManualError,
                  navigate,
                  setIsLoading,
                  setExhibitorError,
                );
                setIsLoading(false);
                setShowProcessingSpinner(false);
              } else {
                trackError("qr_scan_failed", "Invalid badge QR code format", {
                  decoded_text: decodedText,
                });
                alert("Invalid badge QR code.");
                html5QrCode.stop();
                qrScannerRef.current = null; // Clear the ref after stopping
                setShowScanner(false);
              }
            },
            (errorMessage) => {
              // REMOVED: Don't track QR scan errors (fires 10 times per second)
              // These are normal scanning errors when no valid QR code is in frame
            },
          )
          .catch((err) => {
            trackError("qr_scanner_permission_denied", err.message);
            setPermissionDenied(true);
            setShowScanner(false);
          });
      }
    }, 100);
  };

  const closeScanner = () => {
    // Stop the camera if it's running
    if (qrScannerRef.current) {
      // Check if scanner is actually running before stopping
      if (qrScannerRef.current.getState() === 2) {
        // 2 = SCANNING state
        qrScannerRef.current
          .stop()
          .then(() => {
            // Camera stopped successfully
          })
          .catch((err) => {
            console.error("Error stopping camera:", err);
          })
          .finally(() => {
            qrScannerRef.current = null; // Clear the ref
          });
      } else {
        // Scanner not running, just clear the ref
        qrScannerRef.current = null;
      }
    }

    setShowScanner(false);
    setPermissionDenied(false);
  };

  const handleGoToDigitalBackpack = () => {
    trackEvent("digital_backpack_click", {
      page_path: window.location.pathname,
    });

    const connectionDataStr = localStorage.getItem(CONNECTION_DATA_KEY);
    let eventCode: string | undefined;

    if (connectionDataStr) {
      try {
        const connectionData = JSON.parse(connectionDataStr);
        eventCode =
          connectionData.csc ||
          connectionData.cdsEventCode ||
          undefined;
      } catch (error) {
        console.error("Error parsing connection data for event code:", error);
      }
    }

    if (!eventCode) {
      console.warn("Digital Backpack: missing event code");
      return;
    }

    const digitalBackpackUrl = buildDigitalBackpackUrl(
      DIGITAL_BACKPACK_BASE_URL,
      eventCode,
    );

    window.location.href = digitalBackpackUrl;
  };

  const scanAnotherBadge = () => {
    setShowScanner(true);
    setPermissionDenied(false);
    setTimeout(() => {
      if (scannerRef.current) {
        const html5QrCode = new Html5Qrcode("qr-reader");
        qrScannerRef.current = html5QrCode; // Store the scanner instance
        html5QrCode
          .start(
            { facingMode: "environment" },
            { fps: 10, qrbox: 250 },
            async (decodedText) => {
              const badgeObj = parseBadgeUrl(decodedText);
              if (badgeObj) {
                html5QrCode.stop();
                qrScannerRef.current = null;
                // Clear error state only after a valid QR code is decoded
                setExhibitorError(false);
                setValidationError(false);
                setServiceNotAvailable(false);
                setShowScanner(false);
                setShowProcessingSpinner(true);

                // Get cached user badge for connection
                const cachedUserBadge = localStorage.getItem(BADGE_STORAGE_KEY);
                const connectionDataStr = localStorage.getItem(CONNECTION_DATA_KEY);

                let userBadgeObj = null;

                // Try connection data first, then badge storage
                if (connectionDataStr) {
                  try {
                    const connectionData = JSON.parse(connectionDataStr);
                    if (connectionData?.userBadge?.id) {
                      userBadgeObj = {
                        badgeId: connectionData.userBadge.id,
                        lastName: connectionData.userBadge.lastName,
                        firstName: connectionData.userBadge.firstName,
                        companyName: connectionData.userBadge.companyName,
                        showCode: connectionData.userBadge.showCode || "",
                      };
                    }
                  } catch (e) {}
                }

                if (!userBadgeObj && cachedUserBadge) {
                  try {
                    userBadgeObj = JSON.parse(cachedUserBadge);
                  } catch (e) {}
                }

                if (userBadgeObj?.badgeId) {
                  // Direct connection with cached user badge
                  const scannedAttendee = {
                    showId: badgeObj.showCode,
                    badgeId: badgeObj.badgeId,
                    firstName: badgeObj.firstName,
                    lastName: badgeObj.lastName,
                    company: badgeObj.companyName,
                  };

                  setIsLoading(true);
                  await sendAttendeeInfoToApi(
                    userBadgeObj,
                    scannedAttendee,
                    setManualError,
                    navigate,
                    setIsLoading,
                    setExhibitorError,
                  );
                  setIsLoading(false);
                } else {
                  // No cached user badge - navigate to landing page
                  const badgeUrl = `https://l4e.us/${badgeObj.showCode}/${badgeObj.badgeId}/${badgeObj.lastName}/${badgeObj.firstName}/${badgeObj.companyName}`;
                  const params = new URLSearchParams({
                    qr: badgeUrl,
                  });
                  window.location.href = `/?${params.toString()}`;
                }
                setShowProcessingSpinner(false);
              } else {
                alert("Invalid badge QR code.");
                html5QrCode.stop();
                qrScannerRef.current = null;
                setShowScanner(false);
              }
            },
            (errorMessage) => {
              // handle scan errors
            },
          )
          .catch((err) => {
            setPermissionDenied(true);
            setShowScanner(false);
          });
      }
    }, 100);
  };

  return (
    <PageShell>
      {(showProcessingSpinner || showSuccessMessage) && (
        <div className="cdp-splash-overlay">
          <div className="cdp-splash-content">
            <FontAwesomeIcon
              key={showProcessingSpinner ? "processing" : "success"} // force re-render state change
              icon={showProcessingSpinner ? faSpinner : faCircleCheckRegular}
              // Keep FontAwesome's spin prop
              spin={showProcessingSpinner}
              // Add explicit fa-spin class in case custom CSS cancels animation
              className={`cdp-splash-checkmark ${
                showProcessingSpinner ? "fa-spin" : ""
              }`}
              // Fallback inline animation if fa-spin class is overridden
              style={
                showProcessingSpinner
                  ? { animation: "fa-spin 1s linear infinite" }
                  : undefined
              }
            />
            <div className="cdp-splash-text">
              {showProcessingSpinner ? "Connecting..." : "Connection Saved!"}
            </div>
          </div>
        </div>
      )}

      {showLandingContent && (
        <>
          <header className="as-header">
            <h1>
              Welcome to
              <br />
              Attendee Connect
            </h1>
          </header>
          <main className="as-main-content as-main-content--landing">
            {exhibitorError ? (
              <section className="as-exhibitor-panel" aria-label="Exhibitor badge scanned">
                <div className="as-exhibitor-icon-wrap" aria-hidden="true">
                  <div className="as-exhibitor-icon-main">
                    <FontAwesomeIcon icon={faIdCard} />
                  </div>
                  <div className="as-exhibitor-icon-badge">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                  </div>
                </div>
                <h2 className="as-exhibitor-title">Exhibitor Badge Scanned</h2>
                <p className="as-exhibitor-message">
                  To connect, have the exhibitor scan your badge with their lead retrieval device.
                </p>

                <div className="as-exhibitor-info-card" role="note">
                  <FontAwesomeIcon icon={faCircleInfo} className="as-exhibitor-info-icon" />
                  <span>
                    Details will appear in Exhibit Tracker section in Digital Backpack.
                  </span>
                </div>

                {!showScanner && (
                  <button
                    type="button"
                    className="as-exhibitor-scan-btn"
                    onClick={scanAnotherBadge}
                  >
                    <FontAwesomeIcon icon={faQrcode} className="me-2" />
                    Scan Another Badge
                  </button>
                )}

                <button
                  type="button"
                  className="as-exhibitor-tracker-link"
                  onClick={handleGoToDigitalBackpack}
                >
                  Go to Digital Backpack
                </button>

                {permissionDenied && (
                  <div className="alert alert-danger mt-3 text-center">
                    Camera permission denied. Please allow camera access to scan
                    your badge.
                  </div>
                )}
              </section>
            ) : serviceNotAvailable ? (
              // Show service not available error
              <div className="lp-card card mb-3 shadow-sm w-100 border-danger">
                <div className="card-body text-center rounded">
                  {/* Show the attendee's name and company */}
                  {attendee.firstName && attendee.lastName && (
                    <div className="lp-attendee-name fw-semibold mb-2 h5">
                      {attendee.firstName.replace(/-/g, " ")}{" "}
                      {attendee.lastName.replace(/-/g, " ")}
                    </div>
                  )}
                  {attendee.firstName && !attendee.lastName && (
                    <div className="lp-attendee-name fw-semibold mb-2 h5">
                      {attendee.firstName.replace(/-/g, " ")}
                    </div>
                  )}
                  {!attendee.firstName && attendee.lastName && (
                    <div className="lp-attendee-name fw-semibold mb-2 h5">
                      {attendee.lastName.replace(/-/g, " ")}
                    </div>
                  )}
                  {attendee.company && (
                    <div className="lp-attendee-company text-muted mb-3">
                      {attendee.company.replace(/-/g, " ")}
                    </div>
                  )}

                  {/* Error message */}
                  <div className="text-danger mb-3">
                    <FontAwesomeIcon
                      icon={faXmark}
                      size="2x"
                      className="opacity-75"
                    />
                  </div>
                  <div className="h6 text-danger fw-semibold mb-2">
                    Service Not Available
                  </div>
                  <div className="text-muted small">
                    Attendee Connect is not available for this event. Please
                    contact event support for more information.
                  </div>
                </div>
              </div>
            ) : validationError ? (
              // Show validation error message instead of user info
              <div className="lp-card card mb-3 shadow-sm w-100 border-danger">
                <div className="card-body text-center rounded">
                  <div className="text-danger mb-3">
                    <FontAwesomeIcon
                      icon={faXmark}
                      size="2x"
                      className="opacity-75"
                    />
                  </div>
                  <div className="h6 text-danger fw-semibold mb-2">
                    Badge Validation Failed
                  </div>
                  <div className="text-muted small">
                    The scanned badge could not be validated. Please try
                    scanning a different badge or contact support.
                  </div>
                </div>
              </div>
            ) : (
              // Show normal attendee card with new design
              <>
                {/* Handshake icon and "Almost Connected!" */}
                <div className="text-center mb-2 pt-2">
                  <FontAwesomeIcon
                    icon={faHandshake}
                    size="2x"
                    className="text-dark mb-2"
                  />
                  <div className="h4 mb-3 text-center">Almost Connected!</div>
                  <div className="text-muted as-almost-subtitle mb-0 mt-2">
                    One more quick step to complete your connection
                  </div>
                </div>

                {/* Attendee card */}
                <div
                  className="lp-card card mb-0 w-100"
                  style={{
                    backgroundColor: "#dce5cc",
                    borderRadius: "10px",
                    border: "none",
                  }}
                >
                  <div className="card-body text-center rounded py-4">
                    {/* Only display fields if present */}
                    {attendee.firstName && attendee.lastName && (
                      <div className="lp-attendee-name fw-semibold mb-2 h5">
                        {attendee.firstName.replace(/-/g, " ")}{" "}
                        {attendee.lastName.replace(/-/g, " ")}
                      </div>
                    )}
                    {attendee.firstName && !attendee.lastName && (
                      <div className="lp-attendee-name fw-semibold mb-2 h5">
                        {attendee.firstName.replace(/-/g, " ")}
                      </div>
                    )}
                    {!attendee.firstName && attendee.lastName && (
                      <div className="lp-attendee-name fw-semibold mb-2 h5">
                        {attendee.lastName.replace(/-/g, " ")}
                      </div>
                    )}
                    {attendee.title && (
                      <div className="lp-attendee-title fw-semibold text-secondary mb-2">
                        {attendee.title}
                      </div>
                    )}
                    {attendee.company && (
                      <div className="lp-attendee-company text-muted mb-2">
                        {attendee.company.replace(/-/g, " ")}
                      </div>
                    )}
                  </div>
                </div>

                {!exhibitorError &&
                  !validationError &&
                  !serviceNotAvailable && (
                    <section
                      className="as-bottom-fixed-action"
                      aria-label="Scan badge action"
                    >
                      <div className="as-step-indicator" aria-hidden="true">
                        <div className="as-step as-step-complete">
                          <div className="as-step-circle">
                            <FontAwesomeIcon icon={faCheck} />
                          </div>
                          <div className="as-step-label">
                            Their badge scanned
                          </div>
                        </div>
                        <div className="as-step-line" />
                        <div className="as-step as-step-active">
                          <div className="as-step-circle">2</div>
                          <div className="as-step-label">Scan your badge</div>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="as-scan-cta-card"
                        onClick={startScanner}
                        aria-label="Click Here to Scan Your Badge. Camera will open automatically"
                      >
                        <span className="as-scan-cta-title">
                          Click Here to Scan Your Badge
                        </span>
                      </button>
                      <div className="as-scan-cta-subtitle">
                        Camera will open automatically
                      </div>
                      <button
                        type="button"
                        className="as-manual-entry-link"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowManualDrawer(true);
                          setManualError("");
                        }}
                      >
                        Manual entry
                      </button>
                    </section>
                  )}

                <div className="as-bottom-action-spacer" aria-hidden="true" />
              </>
            )}

            {exhibitorError ? null : serviceNotAvailable ? (
              <p>Attendee Connect is not contracted for this event.</p>
            ) : null}

            {(!exhibitorError && (validationError || serviceNotAvailable)) &&
              !showScanner && (
                <button
                  className="btn btn-primary mx-auto d-block mb-3"
                  onClick={scanAnotherBadge}
                >
                  <FontAwesomeIcon icon={faQrcode} className="me-2" />
                  Scan Another Badge
                </button>
              )}

            {permissionDenied && (
              <div className="alert alert-danger mt-3 text-center">
                Camera permission denied. Please allow camera access to scan
                your badge.
              </div>
            )}
          </main>

          {showScanner && (
            <div className="cdp-scanner-overlay">
              <div
                className="cdp-scanner-cam"
                id="qr-reader"
                ref={scannerRef}
              ></div>
              <button
                className="cdp-scanner-close"
                onClick={closeScanner}
                aria-label="Close scanner"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          )}

          {permissionDenied && showScanner && (
            <div className="alert alert-danger cdp-scanner-error">
              Camera permission denied. Please allow camera access to scan
              badges.
            </div>
          )}

          {showManualDrawer && (
            <div
              className="manual-drawer-overlay"
              onClick={() => setShowManualDrawer(false)}
            >
              <div
                className="manual-drawer"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Manual Entry</h5>
                  <button
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowManualDrawer(false)}
                  />
                </div>
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!manualBadgeId.trim() || !manualLastName.trim()) {
                      setManualError("Both fields are required.");
                      return;
                    }
                    setIsLoading(true);
                    setManualError("");
                    setExhibitorError(false); // Reset exhibitor error
                    // Use show code from scanned attendee
                    const showCode = attendee.showId || "";
                    const userBadgeUrl = `https://l4e.us/${showCode}/${manualBadgeId.trim()}/${manualLastName.trim()}/UNKNOWN/UNKNOWN`;
                    const badgeObj = { raw: userBadgeUrl };
                    await sendAttendeeInfoToApi(
                      badgeObj,
                      attendee, // Pass the attendee from URL params
                      setManualError,
                      navigate,
                      setIsLoading,
                      setExhibitorError,
                    );
                    setIsLoading(false);
                  }}
                >
                  <div className="mb-3">
                    <label className="form-label">*Badge ID</label>
                    <input
                      className="form-control"
                      value={manualBadgeId}
                      onChange={(e) => {
                        // Allow only letters and numbers, max 10 chars
                        const value = e.target.value
                          .replace(/[^A-Za-z0-9]/g, "")
                          .replace(/[^A-Za-z0-9]/g, "")
                          .slice(0, 10);
                        setManualBadgeId(value);
                      }}
                      autoFocus
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">*Last Name</label>
                    <input
                      className="form-control"
                      maxLength={40}
                      value={manualLastName}
                      onChange={(e) => {
                        const value = e.target.value.replace(
                          /[^A-Za-zÀ-ÖØ-öø-ÿÑñ'’\-. ]/g,
                          "",
                        );
                        setManualLastName(value);
                      }}
                    />
                  </div>
                  {manualError && (
                    <div className="alert alert-danger py-2">{manualError}</div>
                  )}
                  {exhibitorError && (
                    <div className="as-exhibitor-inline-warning py-2">
                      <strong className="as-exhibitor-inline-warning-title">
                        Attendee Connect only works with attendee badges
                      </strong>
                      <br />
                      <p className="mb-2 fw-semibold mt-2">What to do next:</p>
                      <ol className="mb-0 ps-3 text-start">
                        <li className="mb-1">
                          Ask the exhibitor to scan <em>your</em> badge with
                          their lead retrieval device.
                        </li>
                        <li>
                          Their contact details will appear in your <strong>Exhibit Tracker</strong> section.
                        </li>
                      </ol>
                    </div>
                  )}
                  <button
                    className="btn btn-success"
                    type="submit"
                    disabled={
                      manualBadgeId.trim().length < 2 ||
                      manualLastName.trim().length < 1 ||
                      isLoading
                    }
                  >
                    {isLoading ? (
                      <>
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="me-2"
                        />
                        Saving...
                      </>
                    ) : (
                      "Continue"
                    )}
                  </button>
                </form>
              </div>
            </div>
          )}
        </>
      )}
    </PageShell>
  );
};

// Helper function to check if user is an exhibitor
const checkRegistrationType = (userData: any) => {
  const registrationType =
    userData.registrationType ||
    userData.registration_type ||
    userData.type ||
    userData.userType ||
    userData.user_type ||
    userData.badgeType ||
    userData.badge_type;

  if (!registrationType) {
    console.warn("No registration type found in user data");
    return false;
  }

  const typeStr = registrationType.toString().toLowerCase();

  return (
    typeStr.includes("exhibitor") ||
    typeStr.includes("exhibit") ||
    typeStr === "ex" ||
    typeStr === "e"
  );
};

// Updated to use correct data sources
async function sendAttendeeInfoToApi(
  userBadgeObj: any, // From "Scan My Badge" - the user's own badge
  scannedAttendee: any, // From URL params - the attendee whose badge was scanned
  setManualError: (msg: string) => void,
  navigate: (path: string) => void,
  setIsLoading: (loading: boolean) => void,
  setExhibitorError?: (show: boolean) => void,
) {
  // Resolve showCode per badge - they may belong to different shows
  const userShowCode = userBadgeObj.showCode || "";
  const scannedShowCode = scannedAttendee.showId || "";

  // userBadge comes from scanning "my badge" (QR scanner or manual entry)
  const userBadgeUrl = userBadgeObj.raw
    ? userBadgeObj.raw
    : `https://l4e.us/${userShowCode}/${userBadgeObj.badgeId}/${
        userBadgeObj.lastName
      }/${userBadgeObj.firstName || "UNKNOWN"}/${
        userBadgeObj.companyName || "UNKNOWN"
      }`;

  // scannedBadge comes from URL parameters (the original attendee)
  // Keep hyphens intact - do NOT replace them
  const scannedBadgeUrl = `https://l4e.us/${scannedShowCode}/${
    scannedAttendee.badgeId
  }/${scannedAttendee.lastName || "UNKNOWN"}/${
    scannedAttendee.firstName || "UNKNOWN"
  }/${scannedAttendee.company || "UNKNOWN"}`;

  const apiUrl = `${API_BASE_URL}/connections`;
  const payload = {
    userBadge: userBadgeUrl,
    scannedBadge: scannedBadgeUrl,
  };

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "public, max-age=3600", // Cache validation results in CloudFront
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();

      // Track connection API errors
      trackError("connection_api_failed", errorText, {
        status: response.status,
        user_badge: userBadgeObj.badgeId,
        scanned_badge: scannedAttendee.badgeId,
      });

      // Check if it's an invalid registration type error
      if (response.status === 400 || response.status === 403) {
        try {
          const errorData = JSON.parse(errorText);

          // Check for specific INVALID_REG_TYPE code
          if (errorData.code === "INVALID_REG_TYPE") {
            if (setExhibitorError) {
              setExhibitorError(true);
            }
          }

          // Check for message-based detection
          if (
            errorData.message &&
            (errorData.message
              .toLowerCase()
              .includes("invalid registration type") ||
              errorData.message.toLowerCase().includes("exhibitor") ||
              errorData.message
                .toLowerCase()
                .includes("cannot create connections") ||
              errorData.error
                ?.toLowerCase()
                .includes("invalid registration type") ||
              errorData.error?.toLowerCase().includes("exhibitor"))
          ) {
            if (setExhibitorError) {
              setExhibitorError(true);
            }
            return;
          }
        } catch (parseError) {
          // If JSON parsing fails, check plain text error message
          if (
            errorText.toLowerCase().includes("invalid registration type") ||
            errorText.toLowerCase().includes("exhibitor") ||
            errorText.toLowerCase().includes("cannot create connections") ||
            errorText.includes("INVALID_REG_TYPE")
          ) {
            if (setExhibitorError) {
              setExhibitorError(true);
            }
            return;
          }
        }
      }

      setManualError("Failed to save connection. Please try again.");
      throw new Error(`API request failed: ${response.status} - ${errorText}`);
    }

    const data = await response.json();

    // Check for invalid registration type in success response as well
    if (
      (data.error &&
        (data.error.toLowerCase().includes("invalid registration type") ||
          data.error.toLowerCase().includes("exhibitor"))) ||
      data.code === "INVALID_REG_TYPE"
    ) {
      if (setExhibitorError) {
        setExhibitorError(true);
      }
      return;
    }

    // Validate user badge registration type
    if (data.data?.userBadge && checkRegistrationType(data.data.userBadge)) {
      if (setExhibitorError) {
        setExhibitorError(true);
      }
      return;
    }

    // Also validate scanned badge registration type
    if (
      data.data?.scannedBadge &&
      checkRegistrationType(data.data.scannedBadge)
    ) {
      if (setExhibitorError) {
        setExhibitorError(true);
      } else {
        setManualError(
          "Attendee Connect only works with attendee badges. Ask the exhibitor to scan your badge with their lead retrieval device.",
        );
      }
      return;
    }

    // Track successful connection
    trackEvent("connection_created", {
      connection_id: data.data.id,
      user_badge: userBadgeObj.badgeId,
      scanned_badge: scannedAttendee.badgeId,
      existing_connection: data.data.existingConnection || false,
    });

    // Save connection data to localStorage, merging in showCode per badge
    // since the API may not return showCode in the response
    const dataToStore = { ...data.data };
    const existingConnectionData = localStorage.getItem(CONNECTION_DATA_KEY);
    let persistedEsc = "";
    let persistedCsc = "";

    if (existingConnectionData) {
      try {
        const parsedConnectionData = JSON.parse(existingConnectionData);
        persistedEsc =
          parsedConnectionData.esc ||
          parsedConnectionData.eventXLShowCode ||
          "";
        persistedCsc =
          parsedConnectionData.csc ||
          parsedConnectionData.cdsShowCode ||
          parsedConnectionData.cdsEventCode ||
          "";
      } catch (error) {
        console.error("Error parsing existing connection data:", error);
      }
    }

    const resolvedEsc = persistedEsc || scannedAttendee.esc || "";
    const resolvedCsc = persistedCsc || scannedAttendee.csc || "";

    if (resolvedEsc) {
      dataToStore.esc = resolvedEsc;
    }

    if (resolvedCsc) {
      dataToStore.csc = resolvedCsc;
    }

    if (dataToStore.userBadge && !dataToStore.userBadge.showCode) {
      // For manual entry, user has no showCode — use scanned badge's showCode
      dataToStore.userBadge = {
        ...dataToStore.userBadge,
        showCode: userShowCode || scannedShowCode,
      };
    }
    if (dataToStore.scannedBadge && !dataToStore.scannedBadge.showCode) {
      dataToStore.scannedBadge = {
        ...dataToStore.scannedBadge,
        showCode: scannedShowCode,
      };
    }
    localStorage.setItem(CONNECTION_DATA_KEY, JSON.stringify(dataToStore));
    navigate("/connection-details");
  } catch (error) {
    trackError(
      "connection_network_error",
      error instanceof Error ? error.message : String(error),
      {
        user_badge: userBadgeObj.badgeId,
        scanned_badge: scannedAttendee.badgeId,
      },
    );
    setManualError("Failed to save connection. Please try again.");
    setIsLoading(false);
    console.error("Error sending attendee info to API:", error);
  }
}

// (Removed unused checkExhibitorStatus function)

export default LandingPage;
